/**
 * Race camera: smooth follow, speed-based zoom and screen shake.
 *
 * The camera works in world pixels. The renderer decides the integer scale at
 * which those pixels hit the display, so the camera never needs to know about
 * device pixel ratios.
 */

import { clamp, damp, lerp, type Vec2 } from '@shared/math.js';

export interface CameraTarget {
  x: number;
  y: number;
  /** Forward speed in world px/s, used for zoom and look-ahead. */
  speed: number;
  /** Heading, used to anticipate where the car is going. */
  angle: number;
  vx: number;
  vy: number;
}

export class Camera {
  x = 0;
  y = 0;
  zoom = 1;
  /** Extra zoom multiplier from the player's settings. */
  userZoom = 1;
  /** Whether shake is enabled (accessibility setting). */
  shakeEnabled = true;

  private shakeAmount = 0;
  private shakeTime = 0;
  private lookAheadX = 0;
  private lookAheadY = 0;
  private time = 0;
  /** Rotation applied for a sense of slip; kept tiny so it never nauseates. */
  tilt = 0;

  resize(_width: number, _height: number): void {
    // Nothing to precompute: the renderer derives the view size from zoom.
  }

  /** Snaps immediately, used when a race starts or the player respawns. */
  snapTo(target: CameraTarget): void {
    this.x = target.x;
    this.y = target.y;
    this.lookAheadX = 0;
    this.lookAheadY = 0;
    this.shakeAmount = 0;
    this.tilt = 0;
  }

  /** Reaction to a collision: strength is the impact speed in px/s. */
  shake(strength: number): void {
    if (!this.shakeEnabled) return;
    this.shakeAmount = Math.min(18, this.shakeAmount + strength * 0.075);
  }

  update(target: CameraTarget, dt: number, reducedMotion: boolean): void {
    this.time += dt;
    const speed = Math.abs(target.speed);
    // Look ahead in the direction of travel so corners arrive early.
    const lead = clamp(speed * (reducedMotion ? 0.08 : 0.16), 0, 110);
    const dirX = speed > 12 ? target.vx / Math.max(1, Math.hypot(target.vx, target.vy)) : Math.cos(target.angle);
    const dirY = speed > 12 ? target.vy / Math.max(1, Math.hypot(target.vx, target.vy)) : Math.sin(target.angle);
    const desiredLeadX = dirX * lead;
    const desiredLeadY = dirY * lead;
    const leadRate = damp(3.2, dt);
    this.lookAheadX = lerp(this.lookAheadX, desiredLeadX, leadRate);
    this.lookAheadY = lerp(this.lookAheadY, desiredLeadY, leadRate);

    const followRate = damp(reducedMotion ? 9 : 6.2, dt);
    this.x = lerp(this.x, target.x + this.lookAheadX, followRate);
    this.y = lerp(this.y, target.y + this.lookAheadY, followRate);

    // Zoom out as speed rises so there is time to read the road.
    const speedFraction = clamp(speed / 460, 0, 1.2);
    const targetZoom = (1.06 - speedFraction * 0.2) * this.userZoom;
    this.zoom = lerp(this.zoom, targetZoom, damp(2.2, dt));

    this.shakeAmount = Math.max(0, this.shakeAmount - dt * 22);
    if (!this.shakeEnabled) this.shakeAmount = 0;

    // Subtle tilt into the direction of travel.
    const targetTilt = reducedMotion ? 0 : clamp((target.vx * Math.sin(target.angle) - target.vy * Math.cos(target.angle)) / 900, -0.05, 0.05);
    this.tilt = lerp(this.tilt, targetTilt, damp(4, dt));
  }

  /** Current shake offset in world pixels. */
  shakeOffset(out: Vec2): Vec2 {
    if (this.shakeAmount <= 0.01) {
      out.x = 0;
      out.y = 0;
      return out;
    }
    const t = this.time * 42;
    out.x = Math.sin(t * 1.7) * this.shakeAmount * 0.5 + Math.sin(t * 5.3) * this.shakeAmount * 0.2;
    out.y = Math.cos(t * 2.1) * this.shakeAmount * 0.5 + Math.cos(t * 4.1) * this.shakeAmount * 0.2;
    return out;
  }
}
